import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sparkles, Loader2, Save, GraduationCap, Check } from 'lucide-react';
import { Badge } from '../ui/badge';

const TONES = [
  { value: 'professionale', label: 'Professionale' },
  { value: 'amichevole', label: 'Amichevole' },
  { value: 'lussuoso', label: 'Lussuoso / Elegante' },
  { value: 'giovane', label: 'Giovane / Trendy' },
];
const EMOJI = [
  { value: 'none', label: 'Nessuna emoji' },
  { value: 'few', label: 'Poche (2-3)' },
  { value: 'many', label: 'Molte' },
];
const DAYS = [
  { value: 1, label: 'Lun' }, { value: 2, label: 'Mar' }, { value: 3, label: 'Mer' },
  { value: 4, label: 'Gio' }, { value: 5, label: 'Ven' }, { value: 6, label: 'Sab' }, { value: 7, label: 'Dom' },
];

const BrandBriefTab = () => {
  const { API, getAuthHeaders } = useAuth();
  const [brief, setBrief] = useState(null);
  const [saving, setSaving] = useState(false);
  const [learning, setLearning] = useState(false);
  const [proposal, setProposal] = useState(null);

  const fetchBrief = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai-social/brand-brief`, getAuthHeaders());
      setBrief(res.data);
    } catch (e) {
      console.error('Errore caricamento brand brief:', e);
    }
  }, [API, getAuthHeaders]);

  useEffect(() => { fetchBrief(); }, [fetchBrief]);

  const set = (field, value) => setBrief(prev => ({ ...prev, [field]: value }));
  const toggleDay = (d) => set('preferred_days',
    brief.preferred_days.includes(d) ? brief.preferred_days.filter(x => x !== d) : [...brief.preferred_days, d]);

  const save = async () => {
    setSaving(true);
    try {
      const { user_id, is_default, updated_at, ...payload } = brief;
      await axios.put(`${API}/ai-social/brand-brief`, payload, getAuthHeaders());
      toast.success('Brand Brief salvato. Gli agenti AI useranno queste indicazioni.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const learn = async () => {
    setLearning(true);
    try {
      const res = await axios.post(`${API}/ai-social/brand-brief/learn`, {}, getAuthHeaders());
      setProposal(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossibile analizzare i post');
    } finally {
      setLearning(false);
    }
  };

  const applyProposal = () => {
    const s = proposal.suggestion;
    setBrief(prev => ({
      ...prev, tone: s.tone || prev.tone, emoji: s.emoji || prev.emoji,
      voice_examples: s.voice_examples || prev.voice_examples, target: s.target || prev.target,
      extra_notes: s.extra_notes || prev.extra_notes,
      fixed_hashtags: s.fixed_hashtags || prev.fixed_hashtags,
    }));
    setProposal(null);
    toast.success('Suggerimenti applicati: controlla i campi e salva');
  };

  if (!brief) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="brand-brief-tab">
      <Card>
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" /> Brand Brief per gli agenti AI
          </CardTitle>
          <CardDescription>
            Queste indicazioni vengono usate dal Social Media Manager AI per scrivere post nel tono del tuo salone.
            Più sono precise, migliori saranno i contenuti.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="voice-learn-box">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 text-sm">
                <p className="font-medium flex items-center gap-1.5"><GraduationCap className="w-4 h-4 text-primary" /> Voce del salone</p>
                <p className="text-xs text-muted-foreground">Analizza i post già pubblicati (app, Facebook e Instagram collegati) e compila il brief per te.</p>
              </div>
              <Button variant="outline" size="sm" className="border-primary/40 text-primary" onClick={learn} disabled={learning} data-testid="voice-learn-btn">
                {learning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Impara dai post pubblicati
              </Button>
            </div>
            {proposal && (
              <div className="rounded-md border bg-background p-3 space-y-2 text-sm" data-testid="voice-proposal">
                <p className="text-xs text-muted-foreground">Analizzati {proposal.samples_used} post</p>
                <p>{proposal.analysis.summary}</p>
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="secondary">Tono: {proposal.suggestion.tone}</Badge>
                  <Badge variant="secondary">Emoji: {proposal.suggestion.emoji === 'none' ? 'nessuna' : proposal.suggestion.emoji === 'few' ? 'poche' : 'molte'}</Badge>
                  {(proposal.analysis.recurring_themes || []).map(t => <Badge key={t} variant="outline">{t}</Badge>)}
                </div>
                {proposal.analysis.tone_reason && <p className="text-xs text-muted-foreground italic">{proposal.analysis.tone_reason}</p>}
                {proposal.suggestion.forbidden && <p className="text-xs text-muted-foreground">Da valutare per "Cose da non dire": <span className="italic">{proposal.suggestion.forbidden}</span> (non applicato automaticamente)</p>}
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="ghost" onClick={() => setProposal(null)} data-testid="voice-proposal-dismiss">Ignora</Button>
                  <Button size="sm" onClick={applyProposal} data-testid="voice-proposal-apply"><Check className="w-3.5 h-3.5 mr-1" /> Applica al brief</Button>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tono di voce</Label>
              <Select value={brief.tone} onValueChange={v => set('tone', v)}>
                <SelectTrigger data-testid="brief-tone-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TONES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Uso delle emoji</Label>
              <Select value={brief.emoji} onValueChange={v => set('emoji', v)}>
                <SelectTrigger data-testid="brief-emoji-select"><SelectValue /></SelectTrigger>
                <SelectContent>{EMOJI.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Target clienti</Label>
            <Input data-testid="brief-target-input" value={brief.target} onChange={e => set('target', e.target.value)}
              placeholder="es. donne 30-55 anni della zona, attente alla cura dei capelli, fascia medio-alta" />
          </div>

          <div className="space-y-2">
            <Label>Servizi in evidenza e prezzi indicativi</Label>
            <Textarea data-testid="brief-services-input" rows={3} value={brief.services_notes} onChange={e => set('services_notes', e.target.value)}
              placeholder="es. Balayage da 120€, trattamento cheratina 90€, piega 25€. Specialità: colorazioni naturali." />
            <p className="text-xs text-muted-foreground">I servizi attivi in Impostazioni → Servizi vengono inclusi automaticamente.</p>
          </div>

          <div className="space-y-2">
            <Label>Esempi di caption che ti piacciono</Label>
            <Textarea data-testid="brief-examples-input" rows={4} value={brief.voice_examples} onChange={e => set('voice_examples', e.target.value)}
              placeholder="Incolla 2-3 caption (tue o di riferimento) che rappresentano lo stile che vuoi" />
          </div>

          <div className="space-y-2">
            <Label>Cose da NON dire mai</Label>
            <Textarea data-testid="brief-forbidden-input" rows={2} value={brief.forbidden} onChange={e => set('forbidden', e.target.value)}
              placeholder="es. nomi di competitor, 'low cost', promesse mediche, sconti sopra il 20%" />
          </div>

          <div className="space-y-2">
            <Label>Hashtag fissi del salone</Label>
            <Input data-testid="brief-hashtags-input" value={brief.fixed_hashtags} onChange={e => set('fixed_hashtags', e.target.value)}
              placeholder="#nomesalone #parrucchieremilano" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Giorni preferiti di pubblicazione</Label>
              <div className="flex flex-wrap gap-3 pt-1">
                {DAYS.map(d => (
                  <label key={d.value} className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <Checkbox data-testid={`brief-day-${d.value}`} checked={brief.preferred_days.includes(d.value)} onCheckedChange={() => toggleDay(d.value)} />
                    {d.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Orario preferito</Label>
              <Input data-testid="brief-time-input" type="time" value={brief.preferred_time} onChange={e => set('preferred_time', e.target.value)} className="w-40" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Altre indicazioni</Label>
            <Textarea data-testid="brief-notes-input" rows={2} value={brief.extra_notes} onChange={e => set('extra_notes', e.target.value)}
              placeholder="es. parliamo sempre al femminile, citiamo la titolare per nome (Giulia), chiusi il lunedì" />
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="brief-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salva Brand Brief
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BrandBriefTab;
